import { IonContent, createAnimation, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_DATA, MAX_USERNAME_LENGTH } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OnboardingParamsTool } from '@/pages/Onboarding/utils/paramsTool';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { animationKeyFrames } from './constants';
import { NameStyles } from './Name.styles';

import type { Animation } from '@ionic/react';

export const ScreenName: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Name',
      });
    }

    preloadImage(AI_DATA[webCampaign.aiType].chat.imageSrc);
  }, []);

  const [showInput, setShowinput] = React.useState<boolean>(true);
  const [userName, setUserName] = React.useState<string>('');

  const inputRef = React.useRef<HTMLIonInputElement | null>(null);
  const titleRef = React.useRef<HTMLHeadingElement | null>(null);
  const nameResponseRef = React.useRef<HTMLHeadingElement | null>(null);
  const responseRef = React.useRef<HTMLHeadingElement | null>(null);
  const titleAnimRef = React.useRef<Animation>();
  const responseAnimRef = React.useRef<Animation>();

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const formName = form.watch('name');

  const disableNextStep = !formName;

  const play = async (): Promise<void> => {
    await titleAnimRef.current?.play();
    await responseAnimRef.current?.play();
  };

  const submit = (): void => {
    if (formName) {
      setShowinput(false);
      setUserName(formName);
      OnboardingParamsTool.setParam('name', formName);
      play();
    }
  };

  React.useEffect(() => {
    setUserName('');

    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.getInputElement().then((inputEl) => {
            inputEl.focus();
          });
        }
      });

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.getInputElement().then((inputEl) => {
            inputEl.focus();
          });
        }
      }, 1000);
    }
  }, []);

  React.useEffect(() => {
    if (!titleAnimRef.current) {
      const titleA = createAnimation()
        .addElement(titleRef.current!)
        .keyframes(animationKeyFrames[0])
        .afterAddClass('finish');

      const nameA = createAnimation()
        .addElement(nameResponseRef.current!)
        .keyframes(animationKeyFrames[1])
        .afterAddClass('finish');

      const repsonse = createAnimation()
        .addElement(responseRef.current!)
        .fill('none')
        .duration(1500)
        .easing('linear')
        .keyframes(animationKeyFrames[2])
        .afterAddClass('finishMeet');

      titleAnimRef.current = createAnimation()
        .duration(2000)
        .addAnimation([titleA, nameA, repsonse]);
    }
  }, []);

  const handleNextStep = (): void => {
    wizard.nextStep();
    OnboardingParamsTool.setParam('step', 'chat_example');
  };

  return (
    <NameStyles>
      <OnboardingNavigation />

      <IonContent className="onboarding-name">
        <div className="response">
          <OnboardingTitle ref={titleRef}>
            How should AI characters address you?
          </OnboardingTitle>
          <OnboardingTitle ref={nameResponseRef}>{userName}</OnboardingTitle>
          <OnboardingTitle ref={responseRef} className="start">
            Nice to meet you, {formName}!
          </OnboardingTitle>
        </div>

        <FormInput
          form={form}
          hidden={!showInput}
          disableButton={disableNextStep}
          onSubmit={submit}
          ref={inputRef}
          maxLength={MAX_USERNAME_LENGTH}
          name="name"
          placeholder="Type your name"
          type="text"
        />
      </IonContent>

      <OnboardingFooter
        disabled={disableNextStep}
        onContinue={handleNextStep}
        hidden={showInput}
      />
    </NameStyles>
  );
};
